import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Table } from 'reactstrap';

import { Button, Card, ConfigProvider, DatePicker, Flex, Form, Input, InputNumber, Select, Tooltip } from 'antd';

import classnames from 'classnames';
import dayjs from 'dayjs';
import equal from 'fast-deep-equal';

import { formatDateDifference } from '../../helpers/date';
import { getVisibleFields } from '../../helpers/livestock';

import { DATE_FORMAT, SEARCH_THRESHOLD, SELECT_OPTIONS } from '../../constants/common';
import { GROUP_TYPE, MAIN_GROUP_TYPE } from '../../constants/group';

import ImageUploader from '../../components/ImageUploader';

import styles from './styles.module.scss';

import default_animal from '../../assets/images/default-animal.jpg';
import AnimalPopup from '../animal_popup';
import { COLUMN_2, COLUMN_3, getCOLUMN_1 } from './constants';

function AnimalCard({ animal, isNew = false, className, editMode, formRef, onChange, onPhotoChange, setSubmittable }) {
  const [form] = Form.useForm();

  const { breedsSelectOptions } = useSelector(state => state.breed);
  const { customGroupOptions } = useSelector(state => state.label);
  const { data: animals, stockTypes } = useSelector(state => state.animal);
  const { myPlan } = useSelector(state => state.subscription);
  const { options, loading: optionsLoading } = useSelector(state => state.farm);
  const { types: measurementTypes, loading: typesLoading } = useSelector(state => state.measurement);
  const { user } = useSelector(state => state.user);

  const COLUMN_1 = getCOLUMN_1({ isNew });
  const availableFieldsColumn_1 = getVisibleFields(COLUMN_1, user?.roles?.at(0)?.name, myPlan?.type);
  const availableFieldsColumn_2 = getVisibleFields(COLUMN_2, user?.roles?.at(0)?.name, myPlan?.type);
  const availableFieldsColumn_3 = getVisibleFields(COLUMN_3, user?.roles?.at(0)?.name, myPlan?.type);

  const [initialFormValues, setInitialFormValues] = useState({});
  const [minorMeasurementTypeOptions, setMinorMeasurementTypeOptions] = useState([]);
  const [state, setState] = useState({
    animal,
    animal_popup_field: null,
    filtered_popup_animals: [],
    showPopup: false
  });

  if (formRef) formRef.current = form;

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [values]);

  useEffect(() => {
    if (animal && options) {
      form.setFieldsValue(initFormValues());
    }
  }, [animal, options]);

  useEffect(() => {
    const initial = form.getFieldsValue(true);
    setInitialFormValues(initial);
  }, []);

  useEffect(() => {
    setState(prevState => ({ ...prevState, animal }));
  }, [animal?.id]);

  useEffect(() => {
    const selectedType = measurementTypes?.find(type => type.major_measurement_code === 'LWM');
    const options = selectedType?.measure_minors?.map(measurementTypes => ({
      label: measurementTypes.minor_measurement_name,
      value: measurementTypes.minor_measurement_id,
      uom: measurementTypes.uom
    }));
    setMinorMeasurementTypeOptions(options);
  }, [measurementTypes]);

  useEffect(() => {
    const defaultFarm = options?.find(s => s.value === state.animal?.farm_id) || options?.at(0);
    setState(prev => ({ ...prev, animal: { ...prev.animal, farm_id: defaultFarm?.value } }));
  }, [options]);

  function onLivestockChangeDate(val, field) {
    setState(prevState => ({
      animal: {
        ...prevState.animal,
        [field]: dayjs(val, DATE_FORMAT.FULL_MONTH_DATETIME).format()
      }
    }));
  }

  const onEditAnimalChange = (field, value) => {
    if (field === 'weight' && !value) {
      form.setFieldValue('minor_measurement_type', null);
    }

    setState(prevState => ({
      animal: {
        ...prevState.animal,
        [field]: value
      }
    }));
  };

  const handleSelectChange = (newValue, field) => {
    setState(prevState => ({
      animal: {
        ...prevState.animal,
        [field]: newValue
      }
    }));
  };

  const handleFarmChange = (newValue, newFarm) => {
    setState(prevState => ({
      animal: {
        ...prevState.animal,
        farm_id: newFarm.value,
        geofences: [],
        labels: []
      }
    }));
    // to clear the assigned pasture if any on farm change
    form.setFieldsValue({ geofence_ids: undefined });
  };

  const handleBreedChange = (selectedOption, key) => {
    setState(prevState => ({
      animal: {
        ...prevState.animal,
        [key]: { id: selectedOption.value, display_name: selectedOption.label }
      }
    }));
  };

  async function onEditAnimalFileChange(file) {
    setState(prevState => ({ animal: { ...prevState.animal, new_photo: file } }));

    onPhotoChange(file);
  }

  function showAnimalPopup(key, liveStock) {
    setState(prevState => ({
      ...prevState,
      showPopup: true,
      animal_popup_field: key || null,
      filtered_popup_animals: liveStock || []
    }));
  }

  function closeAnimalPopup() {
    setState(prevState => ({
      ...prevState,
      showPopup: false,
      animal_popup_field: null,
      filtered_popup_animals: []
    }));
  }

  function onAnimalSelect(field, value) {
    form.setFieldValue(field, value);
    onEditAnimalChange(field, value);
    closeAnimalPopup();
  }

  const isDateField = key =>
    key === 'purchase_date' ||
    key === 'date_sold' ||
    key === 'loss_date' ||
    key === 'dob_at' ||
    key === 'last_dip_date' ||
    key === 'last_dose_date';
  const COL1_ROWS = [];
  const COL2_ROWS = [];
  const COL3_ROWS = [];
  const row = ({ key, id, isEdit, label, value, rules, dependencies }) => {
    const formItemConfig = {
      labelAlign: 'left',
      labelWrap: true,
      colon: false,
      name: id,
      label,
      ...(dependencies?.length && { dependencies }),
      ...(rules?.length && {
        rules: key === 'minor_measurement_type' && form.getFieldValue('weight') ? [...rules, { required: true }] : rules
      })
    };
    return (
      <tr key={key}>
        <td>
          {isEdit && editMode ? (
            <Form.Item {...formItemConfig}>{value}</Form.Item>
          ) : (
            <Form.Item label={label} colon={false} labelAlign="left" labelWrap>
              <span className="text-muted text-capitalize">{value}</span>
            </Form.Item>
          )}
        </td>
      </tr>
    );
  };

  const maleLivestocks = animals?.filter(s => s.sex === 'male' && s.id !== animal?.id);
  const femaleLivestocks = animals?.filter(s => s.sex === 'female' && s.id !== animal?.id);

  // COLUMN 1 - DATA
  for (const key of Object.keys(availableFieldsColumn_1)) {
    const label = availableFieldsColumn_1[key]['label'] || '';
    const id = availableFieldsColumn_1[key]['id'] || '';
    const rules = availableFieldsColumn_1[key]['rules'];
    const isEdit = availableFieldsColumn_1[key]['is_editable'] || false;

    let value = animal?.[key];

    if (!(availableFieldsColumn_1[key]?.visibility?.plans ?? []).includes(myPlan?.type)) {
      continue;
    }

    if (isEdit && editMode) {
      if (key === 'eartag_management_id' && value && value.indexOf('mgmt~tmp~') > -1) {
        value = '';
      }

      if (key === 'sex') {
        value = (
          <Select
            className="w-full"
            optionFilterProp="label"
            options={SELECT_OPTIONS.GENDER}
            showSearch={SELECT_OPTIONS.GENDER?.length > SEARCH_THRESHOLD}
            onChange={e => handleSelectChange(e, key)}
          />
        );
      } else if (key === 'farm') {
        value = (
          <Select
            className="w-full"
            optionFilterProp="label"
            loading={optionsLoading}
            options={options}
            showSearch={options?.length > SEARCH_THRESHOLD}
            onChange={handleFarmChange}
          />
        );
      } else if (key === 'specie') {
        value = (
          <Select
            className="text-capitalize w-full"
            optionFilterProp="label"
            options={SELECT_OPTIONS.SPECIE}
            showSearch={SELECT_OPTIONS.SPECIE.length > SEARCH_THRESHOLD}
            onChange={handleSelectChange}
          />
        );
      } else if (key === 'breed') {
        value = state.animal?.[key];
        value = (
          <Select
            className="w-full"
            optionFilterProp="label"
            options={breedsSelectOptions}
            showSearch={breedsSelectOptions?.length > SEARCH_THRESHOLD}
            value={breedsSelectOptions?.filter(s => s.value === value)[0]}
            onChange={(value, option) => handleBreedChange(option, key)}
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          />
        );
      } else if (key === 'stock_type') {
        value = state.animal?.[key];
        let selected_type = { value: value, label: value };
        value = (
          <Select
            className="text-capitalize w-full"
            optionFilterProp="label"
            options={stockTypes}
            showSearch={stockTypes?.length > SEARCH_THRESHOLD}
            value={selected_type}
            onChange={e => handleSelectChange(e, key)}
            optionRender={option => (
              <Tooltip title={option.data.description ? option.data.description : ''} placement="top">
                {option.label}
              </Tooltip>
            )}
          />
        );
      } else if (key === 'polled_horned') {
        value = (
          <Select
            className="w-full"
            optionFilterProp="label"
            options={SELECT_OPTIONS.POLLED_HORNED}
            showSearch={SELECT_OPTIONS.POLLED_HORNED?.length > SEARCH_THRESHOLD}
            value={SELECT_OPTIONS.POLLED_HORNED.find(s => s.value === state.animal?.polled_horned)}
            onChange={e => handleSelectChange(e, key)}
          />
        );
      } else if (key === 'sire_id') {
        value = Number(state.animal?.[key]);
        const livestock = maleLivestocks?.find(s => s.id === +value);
        value = livestock ? livestock?.identifier : 'Select';

        value = (
          <Flex align="center" justify="space-between">
            <Button size="small" type="link" onClick={() => showAnimalPopup(key, maleLivestocks)}>
              {value}
            </Button>
            {value !== 'Select' && (
              <Button
                type="text"
                className="rg-pointer"
                aria-label="Clear selection"
                onClick={() => onEditAnimalChange('', key)}>
                clear
              </Button>
            )}
          </Flex>
        );
      } else if (key === 'dam_id') {
        value = Number(state.animal?.[key]);
        const livestock = femaleLivestocks?.find(s => s.id === +value);
        value = livestock ? livestock.identifier : 'Select';

        value = (
          <Flex align="center" justify="space-between">
            <Button size="small" type="link" onClick={() => showAnimalPopup(key, femaleLivestocks)}>
              {value}
            </Button>
            {value !== 'Select' && (
              <Button
                type="text"
                className="rg-pointer"
                aria-label="Clear selection"
                onClick={() => onEditAnimalChange('', key)}>
                clear
              </Button>
            )}
          </Flex>
        );
      } else if (isDateField(key)) {
        value = (
          <DatePicker
            allowClear={key !== 'dob_at'}
            className="w-full"
            format={DATE_FORMAT.DATE}
            onChange={e => onLivestockChangeDate(e, key)}
          />
        );
      } else if (key === 'geofences') {
        const selectedFarm = options?.find(s => s.value === state.animal?.farm_id);
        const pastures = selectedFarm
          ? selectedFarm.meta.features?.filter(s => !s.is_master)?.map(x => ({ label: x.name, value: x.id }))
          : [];

        value = (
          <Select
            className="w-full"
            optionFilterProp="label"
            disabled={!state.animal?.farm_id}
            options={pastures}
            showSearch={pastures?.length > SEARCH_THRESHOLD}
          />
        );
      } else {
        value = <Input onChange={e => onEditAnimalChange(key, e.target.value)} />;
      }
    } else {
      if (key === 'eartag_management_id' && value && value.indexOf('mgmt~tmp~') > -1) {
        value = '';
      } else if (key === 'sex') {
        if (value === 'male') value = 'Male';
        else if (value === 'female') value = 'Female';
      } else if (key === 'kraal_tag_id') {
        if (state.animal?.[key]) {
          value = state.animal?.[key];
        } else {
          value = animal?.['tag'];
          value = value && value.diagri_id ? <Link to={`/tag/${value.id}`}>{value.diagri_id}</Link> : '';
        }
      } else if (key === 'farm') {
        value = value && value.name ? <Link to={`/farms/${state.animal?.[key]?.id}`}>{value.name}</Link> : '';
      } else if (value && key === 'geofences') {
        value = value.filter(s => (s.name || '').indexOf('Master Geofence') === -1);
        value =
          value && value.length > 0
            ? value
                .map(x => (
                  <Link key={x.id} to={`/pastures/${x.id}`}>
                    {x.name}
                  </Link>
                ))
                .reduce((prev, curr) => [prev, ', ', curr]) || ''
            : '';
      } else if (key === 'sire_id') {
        const livestock = maleLivestocks?.find(s => s.id === animal?.[key]);
        value = livestock ? <Link to={`/animals/${livestock.id}`}>{livestock.identifier}</Link> : '';
      } else if (key === 'dam_id') {
        const livestock = femaleLivestocks?.find(s => s.id === animal?.[key]);
        value = livestock ? <Link to={`/animals/${livestock.id}`}>{livestock.identifier}</Link> : '';
      } else if (value == null) {
        value = '';
      } else if (key === 'dob_at') {
        value = dayjs(value).format(DATE_FORMAT.DATE);
      }
    }

    COL1_ROWS.push(row({ key, id, isEdit, label, value, rules }));
  }

  // COLUMN 2 - DATA
  for (const key of Object.keys(availableFieldsColumn_2)) {
    const label = availableFieldsColumn_2[key]['label'] || '';
    const id = availableFieldsColumn_2[key]['id'] || '';
    const rules = availableFieldsColumn_2[key]['rules'];
    const dependencies = availableFieldsColumn_2[key]['dependencies'];

    const isEdit = availableFieldsColumn_2[key]['is_editable'] || false;
    let value = animal?.[key];

    if (!(availableFieldsColumn_2[key]?.visibility?.plans ?? []).includes(myPlan?.type)) {
      continue;
    }

    if (isEdit && editMode) {
      if (isDateField(key)) {
        value = (
          <DatePicker className="w-full" format={DATE_FORMAT.DATE} onChange={e => onLivestockChangeDate(e, key)} />
        );
      } else if (key === 'minor_measurement_type') {
        value = (
          <Select
            className="text-capitalize w-full"
            optionFilterProp="label"
            disabled={!form.getFieldValue('weight')}
            loading={typesLoading}
            showSearch={minorMeasurementTypeOptions?.length > SEARCH_THRESHOLD}
            options={minorMeasurementTypeOptions}
          />
        );
      } else if (key === 'loss_reason') {
        value = (
          <Select
            allowClear
            className="text-capitalize w-full"
            optionFilterProp="label"
            options={SELECT_OPTIONS.LOSS_REASON}
            showSearch={SELECT_OPTIONS.LOSS_REASON?.length > SEARCH_THRESHOLD}
            onChange={e => handleSelectChange(e, key)}
          />
        );
      } else {
        value =
          key === 'weight' ? (
            <InputNumber className="w-full" min={0} onChange={value => onEditAnimalChange(key, value)} />
          ) : (
            <Input onChange={e => onEditAnimalChange(key, e.target.value)} />
          );
      }
    } else {
      if (isDateField(key) && value && value !== '' && value != '0000-00-00 00:00:00') {
        value = dayjs(new Date(value)).format(DATE_FORMAT.DATE);
      } else if (key === 'calf_livestock_identifier') {
        value = value ? <Link to={`/animals/${state.animal?.['calf_livestock_id']}`}>{value}</Link> : '';
      } else if (key === 'calf_age') {
        value = value ? formatDateDifference(value) : '';
      } else if (key === 'age') {
        const dob = animal?.['dob_at'] || state.animal?.['dob_at'];
        value = dob ? formatDateDifference(animal?.['dob_at'] || state.animal?.['dob_at']) : null;
      } else if (id === 'lastReading') {
        value = animal?.tag?.timestamp_at ? dayjs(animal?.tag?.timestamp_at).format(DATE_FORMAT.DATETIME) : '';
      } else if (id === 'connectionStatus') {
        value = animal?.tag?.status;
      } else if (key === 'minor_measurement_type') {
        value = animal?.weight?.minor_measure?.minor_measurement_name;
      } else if (key === 'weight') {
        value = animal?.weight ? `${animal?.weight?.measurement} ${animal?.weight?.uom}` : null;
      } else if (value == null) {
        value = '';
      }
    }

    COL2_ROWS.push(
      row({
        dependencies,
        id,
        isEdit,
        key,
        label,
        rules,
        value
      })
    );
  }

  // COLUMN 3 - DATA
  for (const key of Object.keys(availableFieldsColumn_3)) {
    const label = availableFieldsColumn_3[key]['label'] || '';
    const isEdit = availableFieldsColumn_3[key]['is_editable'] || false;
    const id = availableFieldsColumn_3[key]['id'] || '';
    const rules = availableFieldsColumn_3[key]['rules'];
    const groups = animal?.labels || [];
    const virtualGroups = groups.filter(item => item.main_type === MAIN_GROUP_TYPE.VIRTUAL);
    const physicalGroups = groups.filter(item => item.main_type === MAIN_GROUP_TYPE.PHYSICAL);
    let value = '';

    if (!(availableFieldsColumn_3[key]?.visibility?.plans ?? []).includes(myPlan?.type)) {
      continue;
    }

    if (key === 'virtual_group') {
      const options = customGroupOptions?.filter(item => item.main_type === MAIN_GROUP_TYPE.VIRTUAL);
      value =
        isEdit && editMode ? (
          <Select
            className="w-full"
            mode="multiple"
            optionFilterProp="label"
            options={options}
            showSearch={options?.length > SEARCH_THRESHOLD}
          />
        ) : (
          virtualGroups.map(x => x.name).join(', ') || ''
        );
    }

    if (key === 'physical_group') {
      const options = customGroupOptions?.filter(item => item.main_type === MAIN_GROUP_TYPE.PHYSICAL);
      value =
        isEdit && editMode ? (
          <Select
            className="w-full"
            optionFilterProp="label"
            options={options}
            showSearch={options?.length > SEARCH_THRESHOLD}
          />
        ) : (
          physicalGroups.map(x => x.name).join(', ') || ''
        );
    }

    COL3_ROWS.push(row({ key, id, isEdit, label, value, rules }));
  }

  function initFormValues() {
    const initValues = {};

    for (const [key, value] of [
      ...Object.entries(availableFieldsColumn_1),
      ...Object.entries(availableFieldsColumn_2),
      ...Object.entries(availableFieldsColumn_3)
    ]) {
      const data = animal?.[value.id];

      if (isDateField(key)) {
        initValues[value.id] = data ? dayjs(data) : null;
      } else if (key === 'virtual_group') {
        const groups = animal?.labels || [];
        const virtualGroups = groups.filter(item => item.main_type === MAIN_GROUP_TYPE.VIRTUAL);

        if (virtualGroups.length) {
          initValues[value.id] = virtualGroups.filter(l => l.type === GROUP_TYPE.CUSTOM).map(item => item.id);
        }
      } else if (key === 'physical_group') {
        const groups = animal?.labels || [];
        const physicalGroups = groups.filter(item => item.main_type === MAIN_GROUP_TYPE.PHYSICAL);

        if (physicalGroups.length) {
          const [selectedGroup] = physicalGroups
            .filter(l => l.type === GROUP_TYPE.CUSTOM)
            .map(item => ({
              main_type: item.main_type,
              label: item.name,
              value: item.id,
              color: item.colour,
              type: item.type
            }));

          initValues[value.id] = selectedGroup?.value;
        }
      } else if (key === 'geofences') {
        const geofence = (animal?.geofences ?? []).find(geofence => !geofence.is_master);
        initValues[value.id] = geofence ? { label: geofence.name, value: geofence.id } : null;
      } else if (key === 'minor_measurement_type') {
        initValues[value.id] = animal?.weight?.minor_measure?.minor_measurement_id;
      } else if (key === 'weight') {
        initValues[value.id] = animal?.weight?.measurement;
      } else if (key === 'sex') {
        initValues[value.id] =
          SELECT_OPTIONS.GENDER.find(s => s.value === state.animal?.sex)?.value ||
          SELECT_OPTIONS.GENDER.find(g => g.isDefault)?.value;
      } else if (key === 'specie') {
        initValues[value.id] =
          SELECT_OPTIONS.SPECIE.find(s => s.value?.toLowerCase() === state.animal?.specie?.toLowerCase())?.value ||
          state.animal?.specie ||
          SELECT_OPTIONS.SPECIE.find(s => s.isDefault)?.value;
      } else if (key === 'farm') {
        initValues[value.id] = options?.find(s => s.value === state.animal?.farm_id)?.value || options?.at(0)?.value;
      } else {
        initValues[value.id] = data;
      }
    }

    return initValues;
  }

  function handleChange() {
    const current = form.getFieldsValue();
    const isSame = equal(current, initialFormValues);

    onChange?.(!isSame);
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 0
          }
        }
      }}>
      <Card className={className}>
        <Form
          labelCol={{ span: 10 }}
          autoComplete="off"
          form={form}
          initialValues={initFormValues()}
          onValuesChange={handleChange}>
          <Flex wrap="wrap-reverse" gap="middle">
            <div className={classnames(styles['animal-card-table'], 'w-full')}>
              <Table className="w-full no-border">
                <tbody>{COL1_ROWS}</tbody>
              </Table>
            </div>
            <div className={classnames(styles['animal-card-table'], 'w-full')}>
              <Table className="w-full no-border">
                <tbody>{COL2_ROWS}</tbody>
              </Table>
            </div>
            <Flex vertical gap="small" className={styles['animal-card-table']}>
              <Card
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: '200px', objectFit: 'contain' }}>
                <img
                  src={(editMode && state.animal?.new_photo) || animal?.photo || default_animal}
                  className="card-img-top"
                  alt="Animal photo"
                  width={351}
                />
              </Card>

              {editMode && (
                <ImageUploader
                  editMode={editMode}
                  file={animal?.photo}
                  url={animal?.photo}
                  handleChange={onEditAnimalFileChange}
                />
              )}

              <Table className="w-full no-border">
                <tbody>{COL3_ROWS}</tbody>
              </Table>
            </Flex>
          </Flex>
        </Form>
      </Card>

      {state.showPopup && (
        <AnimalPopup
          key="animal-popup"
          fieldKey={state.animal_popup_field}
          animals={state.filtered_popup_animals}
          onSelect={onAnimalSelect}
          onClose={closeAnimalPopup}
        />
      )}
    </ConfigProvider>
  );
}

export default memo(AnimalCard);
